<script setup lang="ts">
// Vue
import { computed, onMounted, ref, watch, withDefaults } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";
import { Loader } from "@googlemaps/js-api-loader";
import { MapPin, X } from "@lucide/vue";

// Components
import InputLabel from "./InputLabel.vue";

export interface AddressDetails {
  street: string;
  streetNumber: string;
  city: string;
  postalCode: number | null | string;
  country: string;
}

const props = withDefaults(
  defineProps<{
    value: AddressDetails;
    label?: string;
    placeholder?: string;
    color: string;
    errorMsg?: Array<string | undefined>;
    restrictToCountry?: string | undefined;
    id?: string;
    required?: boolean;
    allowAllCountries?: boolean;
    /** Copy for a fixed-height stand-in shown until the map has an address to confirm. Omit and the map
     *  region collapses instead, which is right where space is tight, like inside a modal. */
    mapPlaceholder?: string;
  }>(),
  {
    restrictToCountry: undefined,
    allowAllCountries: false,
    mapPlaceholder: undefined,
  }
);

const localValue = ref<AddressDetails>({ ...props.value });
const localId = ref<string>(uuidv4());
const inputRef = ref();
const mapRef = ref<google.maps.Map>();
const markerRef = ref<google.maps.marker.AdvancedMarkerElement>();
const localErrorMessages = ref<string[]>([]);

/** The map's own element. A `document.getElementById("map")` lookup used to find it, which meant a second
 *  AddressInput on the page — CreateOwnerModal can open over one — rendered its map into the first's div. */
const mapElement = ref<HTMLDivElement | null>(null);

/** When a Places suggestion last landed, used to let a suggestion win a race against blur. */
const placeCommittedAt = ref(0);
/** True once the Maps libraries have loaded — nothing may touch `google.maps` before that. */
const mapsReady = ref(false);
/** The address the map is currently centred on, so a re-sync doesn't re-geocode the same place. */
const shownOnMap = ref("");
/** True between a click's focus and its mouseup, so the select-all survives the click that caused it. */
const justFocused = ref(false);

const EMPTY_ADDRESS: AddressDetails = { street: "", streetNumber: "", city: "", postalCode: "", country: "" };

/** Whether an address is stored. Drives the clear button and the map, which has nothing to show without one. */
const hasAddress = computed(() => Boolean(localValue.value.street));

/** True once Google has painted tiles at least once. One-way on purpose — resetting on a later
 *  address would flicker back to the stand-in mid re-centre. */
const isMapPainted = ref(false);

/** Whether the map has a place and an instance — needed BEFORE it paints, since Google cannot load tiles
 *  into a zero-height element. Deliberately does not wait for `isMapPainted`. */
const isMapReady = computed(() => Boolean(mapRef.value && hasAddress.value));

/** Whether the map is worth looking at: it has a place and it has drawn it. */
const isMapShowing = computed(() => isMapReady.value && isMapPainted.value);

/** Whether to cover the map's space with a stand-in. Only when a caller supplied the copy for one. */
const isShowingMapPlaceholder = computed(() => Boolean(props.mapPlaceholder) && !isMapShowing.value);

/** Whether the region has any height. The stand-in holds it open; without one, the map's own readiness does. */
const isMapRegionOpen = computed(() => isMapReady.value || isShowingMapPlaceholder.value);

const emits = defineEmits(["update:value"]);

function extractAddressDetail(
  type:
    | "route"
    | "street_number"
    | "locality"
    | "country"
    | "postal_code"
    | "postal_town"
    | "sublocality_level_1"
    | "administrative_area_level_2",
  components: google.maps.GeocoderAddressComponent[] | undefined
) {
  if (!components) return "";
  // Google's `types` is an unordered set — scan the whole array, not just `types[0]`, which
  // silently drops fields when Google reorders tags (e.g. rural Danish addresses).
  const component = components.find((component) => component.types.includes(type));
  return component?.long_name ?? "";
}

/** Takes the components rather than a PlaceResult, so a Geocoder result can reuse it unchanged. */
function extractAddressDetails(components: google.maps.GeocoderAddressComponent[] | undefined) {
  // Street
  let street: string = extractAddressDetail("route", components);

  // Street number
  let streetNumber: string = extractAddressDetail("street_number", components);

  // Postal code
  let postalCode: string = extractAddressDetail("postal_code", components);

  // City — rural addresses sometimes only populate `administrative_area_level_2` (the kommune).
  let city: string =
    extractAddressDetail("locality", components) ||
    extractAddressDetail("postal_town", components) ||
    extractAddressDetail("sublocality_level_1", components) ||
    extractAddressDetail("administrative_area_level_2", components);

  // Country
  let country: string = extractAddressDetail("country", components);

  return {
    street,
    streetNumber,
    city,
    postalCode,
    country,
  };
}

function createFormattedAddress() {
  // Stitch together the address from value - Denmark is default country if nothing exists
  if (!localValue.value.street) return "";
  // Filtered rather than interpolated, because a street number is the one part a real place can lack — a
  // square or a rural address — and interpolating an empty one left "Kongens Nytorv , København".
  const street = [localValue.value.street, localValue.value.streetNumber].filter(Boolean).join(" ");
  return `${street}, ${localValue.value.city}, ${localValue.value.postalCode}, ${localValue.value?.country ?? "Denmark"}`;
}

function googleCreateMap(center: google.maps.MapOptions["center"]) {
  // Create map
  const map = new google.maps.Map(mapElement.value as HTMLElement, {
    center,
    zoom: 14,
    mapId: uuidv4(),
    disableDefaultUI: true,
    gestureHandling: "none",
  });

  // Fires on every paint, not just the first, so moving to a new address waits for its own tiles the same
  // way the first paint does — otherwise the picture keeps filling in for seconds in full view.
  map.addListener("tilesloaded", () => {
    isMapPainted.value = true;
  });

  // Update refs
  mapRef.value = map;

  return map;
}

function googleCreateMarker(map: google.maps.Map, position: google.maps.marker.AdvancedMarkerElementOptions["position"]) {
  // Create marker
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position,
  });

  // Update refs
  markerRef.value = marker;

  return marker;
}

/** Geocode whatever address the field currently holds and put the map + marker on it, creating both on
 *  first use. Callable at any time — not just on mount — since the address can change from outside. */
async function showAddressOnMap() {
  const formattedAddress = createFormattedAddress();

  // Nothing to show, libraries not up yet, or the map is already on this exact address.
  if (!mapsReady.value || !formattedAddress || formattedAddress === shownOnMap.value) return;

  const geoCoderService = new google.maps.Geocoder();

  try {
    const geoCodeResponse = await geoCoderService.geocode({
      address: formattedAddress,
    });

    // Get results
    const geoCodeResult = geoCodeResponse.results as google.maps.GeocoderResult[];

    // Get position
    const position: google.maps.MapOptions["center"] = {
      lat: geoCodeResult[0]?.geometry.location.lat(),
      lng: geoCodeResult[0]?.geometry.location.lng(),
    };

    // Moving an existing map invalidates what it has drawn, so the stand-in goes back over it until the new
    // place paints — showing the OLD address under the new one would be wrong, not just pending.
    const map = mapRef.value ?? googleCreateMap(position);
    if (shownOnMap.value) isMapPainted.value = false;
    map.setCenter(position);

    // Create/move marker
    if (markerRef.value) {
      markerRef.value.position = position;
    } else {
      googleCreateMarker(map, position);
    }

    shownOnMap.value = formattedAddress;
    localErrorMessages.value = [];
  } catch (_) {
    localErrorMessages.value = ["Invalid address!"];
  }
}

/** Select the whole value on focus, so typing replaces it rather than appending — the value is one
 *  atomic token stitched from stored parts, and partial edits produce garbled or incorrect addresses. */
function handleFocus(event: FocusEvent) {
  (event.target as HTMLInputElement).select();
  justFocused.value = true;
}

/** A click ends by collapsing the selection to a caret. Hold the select-all through that one mouseup. */
function handleMouseUp(event: MouseEvent) {
  if (!justFocused.value) return;
  justFocused.value = false;
  event.preventDefault();
}

/** Forget the address, in the field and in the parent together — without both, the input re-renders from
 *  the stored value and puts the old address right back. */
function clearAddress() {
  localValue.value = { ...EMPTY_ADDRESS };
  emits("update:value", { ...EMPTY_ADDRESS });
  localErrorMessages.value = [];
  shownOnMap.value = "";
}

function handleClear() {
  clearAddress();
  inputRef.value?.focus();
}

/** Commit whatever was typed, if it resolves — without this an address typed but never clicked from
 *  suggestions is silently discarded. The 300ms wait guards against a Places click blurring before `place_changed` fires. */
async function handleBlur(event: FocusEvent) {
  const typed = (event.target as HTMLInputElement).value.trim();
  const blurredAt = Date.now();

  // No disabled guard needed: the Location step makes this inert via a native disabled fieldset, which
  // cannot blur. Equality check first, so an empty field over an empty value is a no-op, not a clear.
  if (!mapsReady.value || typed === createFormattedAddress()) return;

  // Deleting the text by hand means the same as pressing the clear button.
  if (!typed) {
    clearAddress();
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, 300));
  if (placeCommittedAt.value > blurredAt) return;

  try {
    const geoCodeResponse = await new google.maps.Geocoder().geocode({
      address: typed,
      ...(props.restrictToCountry ? { componentRestrictions: { country: props.restrictToCountry } } : {}),
    });

    const result = geoCodeResponse.results?.[0];
    if (!result) throw new Error("No match");

    const address = extractAddressDetails(result.address_components);
    emits("update:value", address);
    localValue.value = address;
    localErrorMessages.value = [];
    await showAddressOnMap();
  } catch (_) {
    localErrorMessages.value = ["We could not find that address. Pick one of the suggestions, or check the spelling."];
  }
}

onMounted(async () => {
  const loader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    version: "weekly",
    language: "en",
  });

  loader.importLibrary("core").then(async () => {
    // Load dependencies
    const { Autocomplete } = (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;
    (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
    (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

    mapsReady.value = true;

    if (localValue.value.street) {
      await showAddressOnMap();
    }

    // Bind autocomplete to ref
    const autocomplete = new Autocomplete(inputRef.value, {
      types: ["address"],
    });

    // Set options for autocomplete
    if (props.restrictToCountry) {
      autocomplete.setComponentRestrictions({
        country: [props.restrictToCountry],
      });
    }

    // Set event listener for place changed
    autocomplete.addListener("place_changed", () => {
      // Reset errorMessages
      localErrorMessages.value = [];

      // Get place event
      const place = autocomplete.getPlace();

      // Get position
      const position: google.maps.MapOptions["center"] = {
        lat: place?.geometry?.location?.lat() || 0,
        lng: place?.geometry?.location?.lng() || 0,
      };

      // Create/move map
      const map = mapRef.value ?? googleCreateMap(position);
      map.setCenter(position);

      // Create/move marker
      if (markerRef.value) {
        markerRef.value.position = position;
      } else {
        googleCreateMarker(map, position);
      }

      // Remember when a suggestion landed, so a blur racing this one does not undo it.
      placeCommittedAt.value = Date.now();

      // Emit updated value
      emits("update:value", extractAddressDetails(place.address_components));

      // Set value
      localValue.value = extractAddressDetails(place.address_components);

      // The map already sits on this place — keep the value watcher from geocoding it again.
      shownOnMap.value = createFormattedAddress();
    });
  });
});

/** Follow the address the parent hands down — the value can be replaced from outside (e.g. a revision
 *  preview), and both the field and the map have to move with it. */
watch(
  () => props.value,
  (value) => {
    localValue.value = { ...value };
    showAddressOnMap();
  },
  { deep: true }
);

const color = computed(() => {
  if (props.color === "primary") return "focus:border-app-blue-600";
  return undefined;
});

const allErrors = computed(() => {
  return [...localErrorMessages.value, ...(props.errorMsg ?? [])];
});

const hasErrors = computed(() => allErrors.value.length > 0);
</script>

<template>
  <div class="flex w-full flex-col">
    <InputLabel
      :id="localId"
      :label="label"
      :required="required" />

    <div class="relative w-full">
      <!-- autocomplete="off": otherwise the browser offers the person's own saved address, and its dropdown
           renders over the Google Places suggestions this field depends on. -->
      <input
        :id="localId ? localId : `input-${localId}`"
        ref="inputRef"
        type="text"
        autocomplete="off"
        :value="createFormattedAddress()"
        :placeholder="placeholder"
        class="focus-input rounded-8 bg-slate-0 h-9 w-full border px-3 py-2 text-sm leading-none"
        :class="[color, hasErrors ? 'border-app-red-500' : 'border-slate-300', hasAddress ? 'pr-9' : '']"
        data-cy="address"
        :aria-invalid="hasErrors"
        :aria-describedby="hasErrors ? `${localId}-errors` : undefined"
        @focus="handleFocus"
        @mouseup="handleMouseUp"
        @blur="handleBlur" />

      <!-- Inside a disabled fieldset this is disabled with the input, so a revision preview cannot clear
           an address it is not allowed to save. -->
      <button
        v-if="hasAddress"
        type="button"
        class="focus-ring interactive-active duration-fast ease-standard rounded-4 absolute top-1/2 right-2 -translate-y-1/2 p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        aria-label="Clear address"
        data-cy="address-clear"
        @click="handleClear">
        <X
          class="icon-sm"
          :stroke-width="1.5" />
      </button>
    </div>

    <!-- role="alert": errors arrive on submit, not on typing, so without it a screen reader user got no
         signal when Next failed. Paired with aria-invalid/aria-describedby on the input above. -->
    <div
      v-if="hasErrors"
      :id="`${localId}-errors`"
      role="alert"
      class="bg-app-red-300/20 text-app-red-800 rounded-16 col-span-12 mt-2 flex flex-col gap-2 p-4 text-sm">
      <span
        v-for="error in allErrors"
        :key="error"
        >{{ error }}</span
      >
    </div>

    <!-- One box shared by the stand-in and the map, stacked, so the map is revealed by the stand-in fading
         off it. Height animates only for the no-placeholder case — a stand-in already holds the right size. -->
    <div
      :class="[isMapRegionOpen ? 'mt-4 h-64' : 'h-0', mapPlaceholder ? '' : 'duration-normal ease-standard transition-[height]']"
      class="relative w-full">
      <!-- overflow-hidden clips Google's own tiles to rounded-16. Always mounted since Google needs the element
           to have a size before it fetches. aria-hidden: no controls, gestureHandling "none" — nothing to reach. -->
      <div
        ref="mapElement"
        :class="isMapReady ? 'ring-1 ring-slate-200' : ''"
        class="rounded-16 absolute inset-0 overflow-hidden"
        aria-hidden="true" />

      <!-- Holds space before there's an address and covers the map until it draws. The copy waits for
           `hasAddress`, though: an address already stored (edit wizard) shows a pulsing pin, not a stall message. -->
      <Transition name="fade-in">
        <div
          v-if="isShowingMapPlaceholder"
          class="rounded-16 absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-100 ring-1 ring-slate-200"
          aria-hidden="true">
          <MapPin
            class="icon-lg text-type-muted"
            :class="hasAddress ? 'animate-pulse' : ''"
            :stroke-width="1.5" />
          <p
            v-if="!hasAddress"
            class="t-small text-type-muted">
            {{ mapPlaceholder }}
          </p>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style>
@reference "../../../style.css";

.pac-logo::after {
  @apply hidden;
}
.pac-container {
  @apply rounded-16 mt-[2px] ml-[2px];
  z-index: 9999 !important;
}
.pac-icon {
  @apply hidden;
}
.pac-item {
  @apply px-2 py-1;
}
.pac-item:hover {
}
.pac-item-selected {
  @apply bg-app-blue-600/10;
}
.pac-item-query {
}

.pac-matched {
}
</style>
